import { forwardRef, ForwardedRef } from 'react'

import classNames from 'classnames'

import { isNotNullish } from '@local/utils'

import Image from '@web/components/Image'
import { useGoTo } from '@web/hooks/useGoTo'
import { usePropsViewerMode } from '@web/libs/dev/MyplaceDevtools/usePropsViewer'
import sendClickLog from '@web/libs/nlog/click/sendClickLog'
import { Nullish } from '@web/types'
import Clickable from '@web/ui/components/Clickable'
import FunctionButton from '@web/ui/components/FunctionButton'

import styles from '../UpComingSection.module.scss'
import ProgressBar from './ProgressBar'

interface Props {
  item: {
    id: string
    label?: Nullish<string>
    isCanceled?: boolean
    businessId?: string
    isLoading?: boolean
    businessName: string
    price?: number
    orderNumberText?: string | null
    menuDesc: string
    menuCount?: number
    imageUrl?: string | null
    progressStates: string[]
    stateIndex: number
    isHighlight?: boolean
    isBlackBadge?: boolean
    carDirectionUrl?: {
      appScheme?: string | null
      webUrl?: string | null
    }
    landingUrl?: string
    onClickRefresh?: () => void
    placeId?: string
  }
  ariaHidden?: boolean
  isFlicking?: boolean
  cardIdx?: number
  clickCodes?: {
    cardClick?: string
    refreshIcon?: string
    directionIcon?: string
  }
}
function OrderCardItem(props: Props, ref: ForwardedRef<HTMLLIElement>) {
  const {
    id,
    label,
    isCanceled,
    isLoading,
    businessId,
    businessName,
    price,
    orderNumberText,
    menuDesc,
    menuCount,
    imageUrl,
    progressStates,
    stateIndex,
    isHighlight,
    isBlackBadge,
    landingUrl,
    carDirectionUrl,
    onClickRefresh,
    placeId,
  } = props.item
  const { isFlicking, ariaHidden, cardIdx } = props
  const { goToDirectionLandingUrl } = useGoTo()

  const { PropsViewer } = usePropsViewerMode({ props })

  if (PropsViewer) return <li ref={ref}>{PropsViewer}</li>

  // [D] 조건은 마크업 확인용으로 임시로 넣어놨습니다. 조건에 맞게 이어서 개발 부탁드립니다.
  // - isBookingRequestedOnly: 예약 신청중인 상태
  // - isToday: 오늘 진행 중인 일정
  // const isBookingRequestedOnly = label === '예약확인중'

  const skipFocusProps = ariaHidden
    ? {
        'aria-hidden': true,
        tabIndex: -1,
      }
    : {}

  return (
    <li
      className={classNames(styles.list_item, {
        [styles.type_flicking]: isFlicking,
        [styles.type_today]: isHighlight,
        [styles.type_canceled]: isCanceled,
      })}
      ref={ref}
      {...skipFocusProps}
      id={id}
    >
      <div className={styles.list_item_inner}>
        <Clickable
          className={styles.list_item_link}
          {...skipFocusProps}
          href={landingUrl}
          nClickCode={props.clickCodes?.cardClick}
          nClickCid={id}
          nClickPlaceId={placeId}
          nClickBusinessId={businessId}
          nClickRank={cardIdx ? cardIdx + 1 : undefined}
        >
          <div className={styles.info_area}>
            <div className={styles.tit_area}>
              <div className={styles.tit_inner}>
                {label && (
                  <span className={classNames(styles.label, { [styles.type_checking]: isBlackBadge })}>{label}</span>
                )}
                <span className={styles.name}>{businessName}</span>
              </div>
            </div>
            {orderNumberText && <div className={styles.txt_area}>{orderNumberText}</div>}
            <div className={styles.etc_area}>
              <span className={styles.txt}>{menuDesc}</span>
              {isNotNullish(menuCount) && menuCount > 0 && <span className={styles.count}>외 {menuCount}개</span>}
              {isNotNullish(price) && price > 0 && <em className={styles.price}>{price.toLocaleString()}원</em>}
            </div>
          </div>
          {imageUrl && (
            <div className={styles.thumb_area}>
              <div className="place_thumb">
                <Image src={imageUrl} width={44} height={44} alt="" />
              </div>
            </div>
          )}
        </Clickable>

        {/* 카드내부 하단 영역 */}
        <div className={classNames(styles.card_bottom_area, styles.type_has_progressbar)}>
          <div className={styles.progress_bar_area}>
            <ProgressBar progressStates={progressStates} stateIndex={stateIndex} />
          </div>
          <div className={styles.btn_area}>
            <span className={styles.btn_wrapper}>
              <FunctionButton
                type="REFRESH"
                hideButtonName
                onClick={onClickRefresh}
                clickCode={props.clickCodes?.refreshIcon}
                iconClassName={classNames(styles.ico_area, { [styles.ani_rotate]: isLoading })}
                disabled={isCanceled}
              />
            </span>
            {carDirectionUrl && (
              <span className={styles.btn_wrapper}>
                <FunctionButton
                  type="LOCATION"
                  hideButtonName
                  onClick={() => {
                    if (props.clickCodes?.directionIcon)
                      sendClickLog({
                        area: props.clickCodes.directionIcon,
                        placeId,
                        businessId,
                      })
                    goToDirectionLandingUrl(carDirectionUrl)
                  }}
                  disabled={isCanceled}
                />
              </span>
            )}
          </div>
        </div>
      </div>
    </li>
  )
}

export default forwardRef(OrderCardItem)
